import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AdapterSettingsService } from '../../services/adapter-settings.service';
import { PartnerAdapterSettingsInterface } from '../../types/adapter-settings';
import { UpsertPartnerAdapterSettingsPopupInterface } from '../../types/upsert-adapter-settings';
import { UpsertMasterPartnerAdapterSettingsComponent } from '../upsert-master-partner-adapter-settings/upsert-master-partner-adapter-settings.component';

@Component({
  selector: 'app-master-partner-adapter-settings',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './master-partner-adapter-settings.component.html',
  styleUrl: './master-partner-adapter-settings.component.scss',
})
export class MasterPartnerAdapterSettingsComponent
  implements OnInit, OnDestroy
{
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.adapter;
  filterForm!: FormGroup;
  displayedColumns: string[] = [
    'adapter_name',
    'adapter_type',
    'created_at',
    'action',
  ];
  dataSource!: MatTableDataSource<PartnerAdapterSettingsInterface>;

  partnerList$!: Observable<PartnerListInterface[]>;
  #destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialog,
    private partnerService: PartnerService,
    private messageHandlerService: MessageHandlerService,
    private adapterSettingsService: AdapterSettingsService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.partnerList$ = this.partnerService.sharedData$;
  }

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      [this.attrConstant.partner.key]: ['', Validators.required],
    });
  }

  get selectedPartnerId(): number {
    return this.filterForm.value[this.attrConstant.partner.key];
  }

  listAdapters(): void {
    this.adapterSettingsService
      .listPartnerAdapterSettings({ partner_id: this.selectedPartnerId })
      .pipe(takeUntil(this.#destroy$))
      .subscribe(x => {
        if (x.success) this.dataSource = new MatTableDataSource(x.response);
      });
  }

  addAdapterSetting(): void {
    if (!this.selectedPartnerId) {
      this.messageHandlerService.show('Please select a partner first');
      return;
    }
    const dialogRef = this.dialogRef.open<
      UpsertMasterPartnerAdapterSettingsComponent,
      UpsertPartnerAdapterSettingsPopupInterface
    >(UpsertMasterPartnerAdapterSettingsComponent, {
      disableClose: true,
      data: {
        isUpdate: false,
        partner_id: this.selectedPartnerId,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) this.handelSuccessResponse(result.partnerId);
    });
  }

  updateAdapterSetting(data: PartnerAdapterSettingsInterface): void {
    // Only the ids are handed over: the dialog fetches the record itself so it
    // never edits a stale copy of the table row.
    const dialogRef = this.dialogRef.open<
      UpsertMasterPartnerAdapterSettingsComponent,
      UpsertPartnerAdapterSettingsPopupInterface
    >(UpsertMasterPartnerAdapterSettingsComponent, {
      disableClose: true,
      data: {
        isUpdate: true,
        partner_id: this.selectedPartnerId,
        id: data.id,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) this.handelSuccessResponse(result.partnerId);
    });
  }

  handelSuccessResponse(partnerId: number): void {
    this.filterForm.patchValue({
      [this.attrConstant.partner.key]: partnerId,
    });
    this.listAdapters();
  }

  async deleteAdapterSetting(
    data: PartnerAdapterSettingsInterface
  ): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.adapterSettingsService
        .deletePartnerAdapterSetting({
          partner_id: this.selectedPartnerId,
          id: data.id,
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.listAdapters();
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
